import '../styles/UnexpectedError.scss';

import { WarningCircle } from '@phosphor-icons/react';
import { env } from 'process';
import { useEffect } from 'react';

import { getMode } from '../../../constants/settings';
import { useClassHelper } from '../../../hooks/useClassHelper';
import { useDataDogLogger } from '../../../hooks/useDataDogLogger';
import { Button } from '../../Button/Button';
import { Stack } from '../../Stack/Stack';
import { Heading } from '../../Typography/Heading/Heading';
import { Paragraph } from '../../Typography/Paragraph/Paragraph';

const componentName = 'c-UnexpectedError';

interface Props {
  message?: string;
  isCentered?: boolean;
  onRetry?: () => void;
  error?: Error;
}

export const UnexpectedError = ({
  message = '',
  isCentered,
  onRetry,
  error,
}: Props) => {
  const MODE = getMode();
  const c = useClassHelper(componentName);

  const dataDogLogger = useDataDogLogger();

  useEffect(() => {
    if (!error) return;

    dataDogLogger.error(
      'Unexpected error occurred and was caught by ErrorBoundary',
      {
        context: 'ErrorBoundary',
        location: window.location.href,
        error,
      },
    );
  }, [error]);

  return (
    <Stack
      direction="column"
      gap={3}
      align="center"
      className={c('base', {
        __centered: isCentered,
      })}
    >
      <WarningCircle size={48} />
      <Heading level={2}>Something went wrong!</Heading>
      <Paragraph>
        {message || 'An error occurred. Please reload the page to continue.'}
      </Paragraph>

      {MODE.IS_DEV && error?.message && (
        <Paragraph>
          <strong>Error:</strong> {error.message}
        </Paragraph>
      )}

      <Stack direction="row" gap={2}>
        {onRetry && <Button onClick={onRetry}>Retry</Button>}
        <Button color="secondary" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Stack>
    </Stack>
  );
};
